import { FolderGit2, FolderPlus, GitBranch } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, EmptyState, Panel, plural } from '@orgus/ui';
import { AddProjectDialog } from '../../projects/AddProjectDialog.tsx';
import { Suggestions } from '../../projects/Suggestions.tsx';
import { shortRemote } from '../../projects/model.ts';
import { useOrgus, useMachines, useProjects, useSuggestions } from '../../../lib/store.ts';

/** Step 3: watch a repo. Repos where Claude Code ran recently are one click away. */
export function RepoStep() {
  const navigate = useNavigate();
  const projects = useProjects();
  const suggestions = useSuggestions();
  const machines = useMachines();
  const checkouts = useOrgus((s) => s.checkouts);
  const [adding, setAdding] = useState(false);
  const anyOnline = machines.some((m) => m.online);

  // Suggestions for repos already watched are noise here. A repo just added has its project
  // before its checkouts arrive, so a matching origin counts too.
  const watched = useMemo(() => new Set(Object.values(checkouts).map((c) => `${c.machineId}|${c.path.toLowerCase()}`)), [checkouts]);
  const remotes = useMemo(() => new Set(projects.map((p) => p.remoteUrl).filter((u): u is string => !!u)), [projects]);
  const fresh = useMemo(
    () =>
      suggestions
        .filter((s) => !watched.has(`${s.machineId}|${s.path.toLowerCase()}`) && !(s.remoteUrl && remotes.has(s.remoteUrl)))
        .sort((a, b) => (b.lastSessionAt ?? 0) - (a.lastSessionAt ?? 0)),
    [suggestions, watched, remotes],
  );

  const worktrees = (pid: string) => Object.values(checkouts).filter((c) => c.projectId === pid).length;

  return (
    <div className="flex flex-col gap-4">
      {projects.length > 0 && (
        <Panel title="Watched" icon={<FolderGit2 />} sub={plural(projects.length, 'project')} flush actions={<Button size="sm" variant="ghost" onClick={() => navigate('/projects')}>Open Projects</Button>}>
          <ul>
            {projects.slice(0, 6).map((p) => (
              <li key={p.id} className="flex items-center gap-3 border-b border-border-subtle px-4 py-2 last:border-b-0">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ui font-medium text-fg">{p.name}</span>
                  {p.remoteUrl && <span className="block truncate text-caption text-fg-subtle">{shortRemote(p.remoteUrl)}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-1 text-caption text-fg-subtle num">
                  <GitBranch className="size-3.5" aria-hidden />
                  {worktrees(p.id) ? plural(worktrees(p.id), 'checkout') : 'Reading its checkouts'}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {fresh.length > 0 ? (
        <Suggestions suggestions={fresh.slice(0, 8)} />
      ) : (
        <div className="rounded-card border border-border bg-surface">
          <EmptyState
            icon={<FolderGit2 />}
            title={projects.length ? 'No other repos found' : 'No recent Claude Code sessions found'}
            description={
              anyOnline
                ? 'Repos where Claude Code ran in the last week appear here to add in one click. Add any other git folder by its path.'
                : 'Suggestions come from the machines you connect. Connect this machine first, or add a folder by its path.'
            }
            action={
              <Button variant={projects.length ? 'secondary' : 'primary'} icon={<FolderPlus />} disabled={!anyOnline} onClick={() => setAdding(true)}>
                Add a folder
              </Button>
            }
          />
        </div>
      )}

      {fresh.length > 0 && (
        <p className="text-ui text-fg-muted">
          Not listed?{' '}
          <Button size="sm" variant="ghost" icon={<FolderPlus />} disabled={!anyOnline} onClick={() => setAdding(true)}>
            Add a folder by path
          </Button>
        </p>
      )}

      <p className="text-caption text-fg-subtle">
        Orgus only reads a repo: git status, worktrees and Claude Code transcripts. It never writes to it, and you can stop watching from Projects at any time.
      </p>

      <AddProjectDialog open={adding} onOpenChange={setAdding} />
    </div>
  );
}
